import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

const CURSOR_LABELS = {
  view: 'VIEW →',
  open: 'OPEN →',
  link: 'GO →',
  cv: 'GET CV ↓',
};

const TRAIL_COUNT = 8;
const TRAIL_SIZES = [6, 5.5, 5, 4.5, 4, 3.5, 3, 2];
const TRAIL_OPACITIES = [0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1];
const TRAIL_LERP_FACTORS = [0.25, 0.22, 0.19, 0.16, 0.13, 0.11, 0.09, 0.08];

const CustomCursor = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [cursorLabel, setCursorLabel] = useState('');
  const [isVisible, setIsVisible] = useState(true);

  const mainDotRef = useRef(null);
  const labelRef = useRef(null);
  const trailDotsRef = useRef([]);

  const mousePos = useRef({ x: 0, y: 0 });
  const trailPositions = useRef(
    Array(TRAIL_COUNT)
      .fill(null)
      .map(() => ({ x: 0, y: 0 }))
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    setIsMobile(mediaQuery.matches);

    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    document.body.style.cursor = 'none';

    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };

      if (mainDotRef.current) {
        gsap.set(mainDotRef.current, {
          x: e.clientX,
          y: e.clientY,
        });
      }

      if (labelRef.current) {
        gsap.set(labelRef.current, {
          x: e.clientX + 20,
          y: e.clientY + 20,
        });
      }
    };

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    const handleElementHover = (e) => {
      const target = e.target.closest('[data-cursor]');
      if (target) {
        const cursorType = target.getAttribute('data-cursor');
        const label = CURSOR_LABELS[cursorType] || '';
        setCursorLabel(label);

        if (labelRef.current && label) {
          gsap.to(labelRef.current, {
            scale: 1,
            opacity: 1,
            duration: 0.2,
            ease: 'back.out(1.7)',
          });
        }
      } else {
        setCursorLabel('');
        if (labelRef.current) {
          gsap.to(labelRef.current, {
            scale: 0,
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in',
          });
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseover', handleElementHover);

    let animationFrameId;
    const animate = () => {
      trailPositions.current.forEach((pos, i) => {
        const target = i === 0 ? mousePos.current : trailPositions.current[i - 1];
        const lerpFactor = TRAIL_LERP_FACTORS[i];

        pos.x += (target.x - pos.x) * lerpFactor;
        pos.y += (target.y - pos.y) * lerpFactor;

        if (trailDotsRef.current[i]) {
          gsap.set(trailDotsRef.current[i], {
            x: pos.x,
            y: pos.y,
          });
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      document.body.style.cursor = 'auto';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseover', handleElementHover);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isMobile]);

  if (isMobile) return null;

  return (
    <>
      {/* Trail Dots */}
      {Array.from({ length: TRAIL_COUNT }).map((_, i) => (
        <div
          key={`trail-${i}`}
          ref={(el) => (trailDotsRef.current[i] = el)}
          style={{
            position: 'fixed',
            top: -TRAIL_SIZES[i] / 2,
            left: -TRAIL_SIZES[i] / 2,
            width: TRAIL_SIZES[i],
            height: TRAIL_SIZES[i],
            borderRadius: '50%',
            backgroundColor: '#FFD700',
            opacity: isVisible ? TRAIL_OPACITIES[i] : 0,
            pointerEvents: 'none',
            zIndex: 9998,
            transition: 'opacity 0.3s ease',
          }}
        />
      ))}

      {/* Main Cursor Dot */}
      <div
        ref={mainDotRef}
        style={{
          position: 'fixed',
          top: -5,
          left: -5,
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: '#0a0a0a',
          opacity: isVisible ? 1 : 0,
          pointerEvents: 'none',
          zIndex: 9999,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Cursor Label */}
      <div
        ref={labelRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: '#0a0a0a',
          color: '#FFD700',
          fontFamily: "'Space Mono', monospace",
          fontSize: '11px',
          fontWeight: 600,
          padding: '6px 12px',
          borderRadius: '999px',
          pointerEvents: 'none',
          zIndex: 9999,
          whiteSpace: 'nowrap',
          opacity: 0,
          scale: 0,
          transformOrigin: 'center',
        }}
      >
        {cursorLabel}
      </div>
    </>
  );
};

export default CustomCursor;
